import { BRAND_FILL_LIGHT } from './palette';
/**
 * Per-tenant accent.
 *
 * `/auth/me` returns `brand_primary` on each organization — a hex string, or
 * null to keep Railit ember. When it is set we write the shadcn `--primary`
 * family straight onto `document.documentElement`, which beats both `:root`
 * and `.dark` because inline styles outrank stylesheet rules.
 *
 * Two derived values are computed rather than asked for, so a tenant only ever
 * has to pick one colour:
 *   - the *foreground* that sits on top of the accent (black or white,
 *     whichever has more contrast against it);
 *   - the *text* variant, nudged darker or lighter until it clears 4.5:1
 *     against the current page background — the raw accent is usually a fill
 *     colour and rarely readable as body text.
 */

import type { ResolvedTheme } from './theme';

/* Generated from brand/palette.json — see `npm run brand`. */
const WEHI_EMBER = BRAND_FILL_LIGHT;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function parseHex(input: string | null | undefined): Rgb | null {
  if (!input) return null;
  const hex = input.trim().replace(/^#/, '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[r, g, b].map((n) => clamp(n).toString(16).padStart(2, '0')).join('')}`;
}

function channelLuminance(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance, 0 (black) → 1 (white). */
export function luminance(rgb: Rgb): number {
  return (
    0.2126 * channelLuminance(rgb.r) +
    0.7152 * channelLuminance(rgb.g) +
    0.0722 * channelLuminance(rgb.b)
  );
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const BLACK: Rgb = { r: 10, g: 8, b: 7 };
const WHITE: Rgb = { r: 255, g: 255, b: 255 };

/** Black or white — whichever reads better on top of `rgb`. */
export function readableForeground(rgb: Rgb): string {
  return contrastRatio(rgb, BLACK) >= contrastRatio(rgb, WHITE)
    ? toHex(BLACK)
    : toHex(WHITE);
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

/**
 * Walk the accent toward black (on light pages) or white (on dark pages) in
 * small steps until it clears `target` contrast against the page background.
 * Returns the original colour untouched when it already passes.
 */
export function readableAccentText(accent: Rgb, theme: ResolvedTheme, target = 4.5): string {
  const bg: Rgb = theme === 'dark' ? { r: 12, g: 10, b: 9 } : { r: 252, g: 251, b: 250 };
  const toward = theme === 'dark' ? WHITE : { r: 0, g: 0, b: 0 };
  let candidate = accent;
  for (let step = 0; step <= 20; step += 1) {
    candidate = mix(accent, toward, step / 20);
    if (contrastRatio(candidate, bg) >= target) return toHex(candidate);
  }
  return toHex(candidate);
}

const BRAND_VARS = ['--brand', '--brand-foreground', '--brand-ink'] as const;

/**
 * Paint (or clear) the tenant accent. Pass `null` to fall back to the stylesheet
 * default, which is Railit ember.
 */
export function applyBrandAccent(hex: string | null | undefined, theme: ResolvedTheme): void {
  const root = document.documentElement;
  const rgb = parseHex(hex);
  if (!rgb || toHex(rgb).toLowerCase() === WEHI_EMBER) {
    for (const name of BRAND_VARS) root.style.removeProperty(name);
    return;
  }
  root.style.setProperty('--brand', toHex(rgb));
  root.style.setProperty('--brand-foreground', readableForeground(rgb));
  root.style.setProperty('--brand-ink', readableAccentText(rgb, theme));
}
